import React, { useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  InputGroup,
  InputRightElement,
  Icon,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import toast from "react-hot-toast";
import { createAPIEndPoint } from "config/api/api";

const ResetPasswordModal = ({ isOpen, onClose, update_id }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false); // 🔑 New loading state


  const handlePasswordVisibility = () => setShowPassword(!showPassword);
  const handleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleResetPassword = async (values) => {
    try {
      setLoading(true); // start loading
      const data = {
        user_id: update_id,
        new_password: values.newPassword,
        confirm_password: values.confirmPassword,
      };
      const response = await createAPIEndPoint("admin_change_password").create(
        data
      );
      toast.success(response.data.message || "Password reset successfully!");
      onClose?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
        "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false); // stop loading
    }
  };


  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (values) => handleResetPassword(values),
  });

  return (
    <>


      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" pb="16px">
          <ModalHeader color="brand.500"> Reset Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form onSubmit={formik.handleSubmit}>
              {/* New Password */}
              <FormControl isInvalid={formik.touched.newPassword && formik.errors.newPassword} mb={4}>
                <FormLabel>New Password</FormLabel>
                <InputGroup>
                  <Input
                    variant="auth"
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <InputRightElement>
                    <Icon as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye} onClick={handlePasswordVisibility} cursor="pointer" />
                  </InputRightElement>
                </InputGroup>
                {formik.touched.newPassword && formik.errors.newPassword && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors.newPassword}
                  </Text>
                )}
              </FormControl>

              {/* Confirm Password */}
              <FormControl isInvalid={formik.touched.confirmPassword && formik.errors.confirmPassword} mb={4}>
                <FormLabel>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    variant="auth"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <InputRightElement>
                    <Icon as={showConfirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye} onClick={handleConfirmPasswordVisibility} cursor="pointer" />
                  </InputRightElement>
                </InputGroup>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors.confirmPassword}
                  </Text>
                )}
              </FormControl>

              {/* Submit Button */}
              <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={loading}>
                Reset Password
              </Button>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ResetPasswordModal;
